import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Star } from "lucide-react";
import { getTeam } from "../lib/api";
import { useFavorite } from "../hooks/useFavorite";
import { formatKickoff } from "../lib/format";

export const FavoriteBanner = () => {
  const { fav } = useFavorite();
  const { data } = useQuery({
    queryKey: ["team", fav],
    queryFn: () => getTeam(fav),
    enabled: !!fav,
  });

  if (!fav) return null;
  if (!data) return null;

  const { team, games } = data;
  const nextGame = games.find((g) => g.status === "scheduled");
  const lastGame = [...games].reverse().find((g) => g.status !== "scheduled");

  return (
    <section
      data-testid="favorite-banner"
      className="relative border border-primary/60 overflow-hidden"
      style={{ background: `linear-gradient(90deg, ${team.color}, rgba(0,0,0,0.9))` }}
    >
      <div className="relative px-5 sm:px-8 py-5 flex flex-col md:flex-row md:items-center gap-5">
        <Link to={`/team/${team.id}`} className="flex items-center gap-4 shrink-0">
          <img src={team.logo} alt={team.name} className="w-14 h-14 object-contain" />
          <div>
            <p className="flex items-center gap-1.5 text-xs uppercase tracking-widest text-primary">
              <Star className="w-3.5 h-3.5 fill-primary" /> Your Team
            </p>
            <p className="font-display text-3xl leading-none">{team.fullName}</p>
            <p className="text-sm text-white/80">{team.wins}-{team.losses} · {team.streak} · {team.division}</p>
          </div>
        </Link>

        <div className="md:ml-auto flex flex-wrap gap-3">
          {lastGame && (
            <div data-testid="fav-last-game" className="border border-white/20 bg-black/40 px-4 py-2">
              <p className="label-caps text-white/60">Last</p>
              <p className="font-semibold text-sm">
                {lastGame.awayTeam} {lastGame.awayScore} @ {lastGame.homeTeam} {lastGame.homeScore}
              </p>
            </div>
          )}
          {nextGame && (
            <Link
              to={`/schedule?week=${nextGame.week}`}
              data-testid="fav-next-game"
              className="border border-primary bg-black/40 px-4 py-2 hover:bg-primary hover:text-black transition-colors duration-150 group"
            >
              <p className="label-caps text-primary group-hover:text-black">Next · Wk {nextGame.week}</p>
              <p className="font-semibold text-sm">
                {nextGame.homeTeam === team.abbr
                  ? `vs ${nextGame.awayTeam}`
                  : `@ ${nextGame.homeTeam}`}{" "}
                · {formatKickoff(nextGame.kickoff).date} {formatKickoff(nextGame.kickoff).time}
              </p>
            </Link>
          )}
        </div>
      </div>
    </section>
  );
};
